import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import type { Connection, Edge, EdgeTypes, IsValidConnection, NodeTypes } from '@xyflow/react';
import { Skeleton } from '@ui/skeleton.tsx';
import {
  useApiAdminusersGetCollection,
  useApiAdminsalesHierarchyLinksPost
} from '@r/api/generated/client.ts';
import type { UserUserCollectionReadModel } from '@r/api/generated/client.schemas.ts';
import { isOrvalErrorFromPath } from '@r/api/orval-error-helper.ts';
import { useOrvalErrorToast } from '@r/hooks/use-orval-error-toast.ts';
import { useSalesHierarchyTree } from '@admin/hooks/use-sales-hierarchy-tree.ts';
import { useHierarchyRelationActions } from '@admin/hooks/use-hierarchy-relation-actions.ts';
import { TreeFlowCanvas } from '@admin/components/flow/tree-flow-canvas.tsx';
import { TreeFlowNode } from '@admin/components/flow/tree-flow-node.tsx';
import { TreeFlowDeleteEdge } from '@admin/components/flow/tree-flow-delete-edge.tsx';
import { TreeFlowLeafClusterNode } from '@admin/components/flow/tree-flow-leaf-cluster-node.tsx';
import {
  isLeafClusterNodeId,
  type TreeFlowPosition
} from '@admin/components/flow/tree-flow-layout.ts';
import {
  HIERARCHY_SALE_DRAG_TYPE,
  useHierarchyCanvasStore
} from '@admin/components/hierarchy/hierarchy-canvas-store.ts';
import { buildHierarchyFlowGraph, type HierarchyFlowNode } from './sales-hierarchy-flow-utils.ts';
import { validateHierarchyLink } from './sales-hierarchy-flow-rules.ts';
import { buildCreateLinkPayload, invalidateSalesHierarchyTreeQueries } from './hierarchy-utils.ts';
import { buildHierarchyCanvasGraph } from './sales-hierarchy-canvas-graph.ts';
import { HierarchyRelationCascadeDialog } from './hierarchy-relation-cascade-dialog.tsx';

const nodeTypes: NodeTypes = {
  hierarchy: TreeFlowNode,
  'hierarchy-leaf-cluster': TreeFlowLeafClusterNode
};
const edgeTypes: EdgeTypes = { 'hierarchy-delete': TreeFlowDeleteEdge };

interface SalesHierarchyCanvasProps {
  hierarchyId: number;
  canManage: boolean;
}

const linkErrorKeys: Record<'self_link' | 'cycle', string> = {
  self_link: 'admin.hierarchy.link.create.error.self_link',
  cycle: 'admin.hierarchy.link.create.error.cycle'
};

/**
 * L'id d'un nœud cluster ne correspond à aucune personne : quand la
 * connexion part d'une carte à l'intérieur, la vraie personne est portée
 * par `sourceHandle` (poignée de cette carte précise), pas par `source`
 * (id du cluster lui-même). Pure, ne dépend d'aucun état du composant.
 */
function resolveConnectionSourceId(connection: Connection | Edge): string | null {
  if (!connection.source) return null;
  return isLeafClusterNodeId(connection.source)
    ? (connection.sourceHandle ?? null)
    : connection.source;
}

/**
 * Canvas de la hiérarchie : fetch l'arbre + la liste complète des
 * ROLE_SALES_ADMIN du workspace (pour résoudre l'id numérique de n'importe
 * quel nœud, affiché ou fraîchement ajouté). Un sale se glisse-dépose depuis
 * la sidebar dédiée (`HierarchySidebar`) directement sur le canvas ; le
 * glisser-déposer entre 2 nœuds du canvas crée le vrai lien.
 */
export function SalesHierarchyCanvas({ hierarchyId, canManage }: SalesHierarchyCanvasProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const showOrvalErrorToast = useOrvalErrorToast();
  const { tree, isTreeLoading } = useSalesHierarchyTree(hierarchyId);
  const [stagedNodes, setStagedNodes] = useState<HierarchyFlowNode[]>([]);
  /** Position figée au moment du drop (depuis la sidebar), par id de nœud staged. */
  const [stagedPositions, setStagedPositions] = useState<Map<string, TreeFlowPosition>>(new Map());
  const setPlacedUniqueCodes = useHierarchyCanvasStore((state) => state.setPlacedUniqueCodes);

  const salesQuery = useApiAdminusersGetCollection({
    'maxRole[]': ['ROLE_SALES_ADMIN'],
    perPage: 500,
    'order[firstname]': 'asc'
  });

  /** Table uniqueCode → id numérique UserWorkspace, seule donnée manquante des lectures d'arbre. */
  const userWorkspaceIdByUniqueCode = useMemo(() => {
    const map = new Map<string, number>();
    for (const user of (salesQuery.data?.data ?? []) as UserUserCollectionReadModel[]) {
      const userWorkspace = user.userWorkspaces?.[0];
      if (userWorkspace?.uniqueCode && userWorkspace.id != null) {
        map.set(userWorkspace.uniqueCode, userWorkspace.id);
      }
    }
    return map;
  }, [salesQuery.data?.data]);

  /** Table uniqueCode → nom complet, pour étiqueter un nœud déposé depuis la sidebar (payload du drop = juste l'uniqueCode). */
  const labelByUniqueCode = useMemo(() => {
    const map = new Map<string, string>();
    for (const user of (salesQuery.data?.data ?? []) as UserUserCollectionReadModel[]) {
      const uniqueCode = user.userWorkspaces?.[0]?.uniqueCode;
      if (uniqueCode) map.set(uniqueCode, user.fullname);
    }
    return map;
  }, [salesQuery.data?.data]);

  const { nodes: treeNodes, links } = useMemo(() => buildHierarchyFlowGraph(tree ?? []), [tree]);

  /** Nœuds affichés = ceux de l'arbre + ceux ajoutés localement, pas encore reliés. */
  const allNodes = useMemo(() => {
    const treeIds = new Set(treeNodes.map((node) => node.id));
    return [...treeNodes, ...stagedNodes.filter((node) => !treeIds.has(node.id))];
  }, [treeNodes, stagedNodes]);

  /** Têtes réelles de la hiérarchie (pas les nœuds staged sans parent, ceux-là n'en sont pas). */
  const treeRootIds = useMemo(
    () => new Set(treeNodes.filter((node) => node.parentId === null).map((node) => node.id)),
    [treeNodes]
  );

  /** Publié pour la sidebar (frère dans l'arbre de composants, cf. `hierarchy-canvas-store.ts`). */
  useEffect(() => {
    setPlacedUniqueCodes(new Set(allNodes.map((node) => node.id)));
    return () => setPlacedUniqueCodes(new Set());
  }, [allNodes, setPlacedUniqueCodes]);

  const {
    deletingLinkId,
    cascadeChoice,
    confirmRelationAction,
    confirmCascadeChoice,
    cancelCascadeChoice
  } = useHierarchyRelationActions({
    t,
    queryClient,
    hierarchyId,
    showOrvalErrorToast,
    allNodes,
    setStagedNodes
  });

  const createLinkMutation = useApiAdminsalesHierarchyLinksPost({
    mutation: {
      onSuccess: (_data, variables) => {
        toast.success(t('admin.hierarchy.link.create.success'));
        invalidateSalesHierarchyTreeQueries(queryClient, hierarchyId);
        // Les nœuds concernés viennent maintenant de l'arbre (refetch) : on les
        // retire de la liste locale (et leur position figée) pour éviter un
        // doublon le temps du refetch.
        const { parent, child } = variables.data;
        const isNowLinked = (node: HierarchyFlowNode) => {
          const iri = userWorkspaceIdByUniqueCode.get(node.id);
          return (
            iri != null &&
            (`/user_workspaces/${iri}` === parent || `/user_workspaces/${iri}` === child)
          );
        };
        setStagedNodes((snapshot) => snapshot.filter((node) => !isNowLinked(node)));
        setStagedPositions((snapshot) => {
          const next = new Map(snapshot);
          for (const node of stagedNodes) {
            if (isNowLinked(node)) next.delete(node.id);
          }
          return next;
        });
      },
      onError: (error) => {
        if (isOrvalErrorFromPath(error, 'post', '/admin/sales-hierarchy-links')) {
          showOrvalErrorToast(error, t('admin.hierarchy.link.create.error'));
        } else {
          toast.error(t('admin.hierarchy.link.create.error'));
        }
      }
    }
  });

  const isValidConnection: IsValidConnection = (connection) => {
    const source = resolveConnectionSourceId(connection);
    if (!canManage || !source || !connection.target) return false;
    return validateHierarchyLink(allNodes, source, connection.target).isValid;
  };

  const handleConnect = (connection: Connection) => {
    const source = resolveConnectionSourceId(connection);
    if (!canManage || !source || !connection.target) return;

    const validation = validateHierarchyLink(allNodes, source, connection.target);
    if (!validation.isValid) {
      toast.error(t(linkErrorKeys[validation.reason]));
      return;
    }

    const parentUserWorkspaceId = userWorkspaceIdByUniqueCode.get(source);
    const childUserWorkspaceId = userWorkspaceIdByUniqueCode.get(connection.target);
    if (parentUserWorkspaceId == null || childUserWorkspaceId == null) {
      toast.error(t('admin.hierarchy.link.create.error'));
      return;
    }

    createLinkMutation.mutate({
      data: buildCreateLinkPayload(hierarchyId, parentUserWorkspaceId, childUserWorkspaceId)
    });
  };

  /** Dépose un sale glissé depuis la sidebar, exactement à l'endroit lâché. */
  const handleExternalDrop = (uniqueCode: string, position: TreeFlowPosition) => {
    if (allNodes.some((node) => node.id === uniqueCode)) return; // déjà présent (sécurité, la sidebar filtre déjà)
    const label = labelByUniqueCode.get(uniqueCode) ?? uniqueCode;
    setStagedNodes((snapshot) => [...snapshot, { id: uniqueCode, parentId: null, label }]);
    setStagedPositions((snapshot) => new Map(snapshot).set(uniqueCode, position));
  };

  /**
   * Retire un nœud sans aucun lien (jamais relié, ou détaché via les
   * ciseaux) du canvas — purement local, aucun appel back (rien à supprimer
   * en BDD, il n'a pas de lien). Il redevient dispo dans la sidebar
   * automatiquement (dérivé de `allNodes` via `hierarchy-canvas-store`).
   */
  const handleRemoveFromCanvas = (nodeId: string) => {
    const name = allNodes.find((node) => node.id === nodeId)?.label ?? nodeId;
    toast(t('admin.hierarchy.relation.remove.confirm', { name }), {
      action: {
        label: t('label.confirm'),
        onClick: () => {
          setStagedNodes((snapshot) => snapshot.filter((node) => node.id !== nodeId));
          setStagedPositions((snapshot) => {
            const next = new Map(snapshot);
            next.delete(nodeId);
            return next;
          });
        }
      },
      cancel: { label: t('label.cancel'), onClick: () => {} }
    });
  };

  // Construction des nodes/edges React Flow (layout dagre + clustering des
  // feuilles + câblage des actions) extraite dans `sales-hierarchy-canvas-graph.ts`
  // (composant devenu trop volumineux) — pure, appelée telle quelle ici.
  const { nodes, edges } = useMemo(
    () =>
      buildHierarchyCanvasGraph({
        allNodes,
        links,
        canManage,
        deletingLinkId,
        stagedPositions,
        treeRootIds,
        t,
        onTrash: confirmRelationAction,
        onRemoveFromCanvas: handleRemoveFromCanvas
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allNodes, links, canManage, deletingLinkId, stagedPositions, treeRootIds, t]
  );

  if (isTreeLoading) {
    return <Skeleton className="w-full h-full" />;
  }

  return (
    <>
      <TreeFlowCanvas
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        defaultEdgeType="hierarchy-delete"
        nodesConnectable={canManage}
        isValidConnection={isValidConnection}
        onConnect={handleConnect}
        externalDragType={canManage ? HIERARCHY_SALE_DRAG_TYPE : undefined}
        onExternalDrop={handleExternalDrop}
        className="w-full h-full bg-[hsl(293_6%_90%)]"
      />

      <HierarchyRelationCascadeDialog
        t={t}
        choice={cascadeChoice}
        onConfirm={confirmCascadeChoice}
        onCancel={cancelCascadeChoice}
      />
    </>
  );
}
